import { BaseTool } from '../../core/BaseTool';
import { UploadManager } from '../image-converter/uploadManager';
import { ConversionManager } from '../image-converter/conversionManager';
import { FormatSelector } from '../../ui/formatSelector';
import { AppIcon } from '../image-generator/appIcon';
import { Modal } from '../../ui/modal';
import { TaglineManager } from '../image-generator/taglineManager';
import { CookieConsentController } from '../../services/cookieConsentController';
import { downloadBlob } from '../../utils/downloadHelper';

export type EditorMode = 'crop' | 'resize';

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * ImageEditorTool - Base class for Image Crop and Image Resize tools
 * Handles common functionality like upload, preview, format selection, and download
 */
export class ImageEditorTool extends BaseTool {
  protected uploadManager: UploadManager;
  protected conversionManager: ConversionManager;
  protected formatSelector: FormatSelector;
  protected appIcon: AppIcon;
  protected modal: Modal;

  protected currentImage: HTMLImageElement | null = null;
  protected currentFile: File | null = null;
  protected selectedFormat: string = 'png';
  protected mode: EditorMode;

  protected originalDimensions: ImageDimensions = { width: 0, height: 0 };
  protected newDimensions: ImageDimensions = { width: 0, height: 0 };
  protected cropArea: CropArea = { x: 0, y: 0, width: 0, height: 0 };
  protected aspectRatioLocked: boolean = true;
  protected aspectRatio: number = 1;
  protected displayScaleFactor: number = 1; // Tracks when preview hits container boundary

  constructor(mode: EditorMode) {
    super();
    this.mode = mode;
    this.uploadManager = new UploadManager();
    this.conversionManager = new ConversionManager();
    this.formatSelector = new FormatSelector();
    this.modal = new Modal();
    this.appIcon = new AppIcon(this.modal);
  }

  async init(): Promise<void> {
    console.log(`Initializing Image Editor Tool in ${this.mode} mode...`);

    // Initialize app icon
    this.appIcon.setRandomIcon();

    // Initialize tagline (appends random tagline to description)
    new TaglineManager();

    // Initialize cookie consent
    new CookieConsentController();

    // Register modal globally for thank you popup
    (window as any).appModal = this.modal;

    // Initialize format selector
    this.formatSelector.init(
      'formatSelectorContainer',
      ['png', 'jpeg', 'webp'],
      'png',
      this.handleFormatChange.bind(this)
    );

    // Initialize upload manager
    this.uploadManager.init(this.handleFileUpload.bind(this));

    // Setup event listeners
    this.setupEventListeners();

    // Initialize mode-specific UI
    this.initModeSpecificUI();

    this.setInitialized(true);
    console.log('Image Editor Tool initialized');
  }

  protected initModeSpecificUI(): void {
    const cropOverlay = document.getElementById('cropOverlay');
    const resizeOverlay = document.getElementById('resizeOverlay');
    const resizeControls = document.getElementById('resizeControls');
    const cropAspectPresets = document.getElementById('cropAspectPresets');
    const editorSectionTitle = document.getElementById('editorSectionTitle');

    if (this.mode === 'crop') {
      if (cropOverlay) cropOverlay.style.display = 'block';
      if (cropAspectPresets) cropAspectPresets.style.display = 'flex';
      if (editorSectionTitle) editorSectionTitle.textContent = 'Crop Image';
    } else if (this.mode === 'resize') {
      if (resizeOverlay) resizeOverlay.style.display = 'block';
      if (resizeControls) resizeControls.style.display = 'flex';
      if (editorSectionTitle) editorSectionTitle.textContent = 'Resize Image';
    }
  }

  protected setupEventListeners(): void {
    // Generate/Download button
    const generateBtn = document.getElementById('generateBtn');
    generateBtn?.addEventListener('click', () => {
      this.processAndDownload();
    });

    // Clear image button
    const clearBtn = document.getElementById('clearImageBtn');
    clearBtn?.addEventListener('click', () => {
      this.clearImage();
    });

    // Keep preview sizing in sync with viewport changes
    window.addEventListener('resize', () => {
      if (!this.currentImage) return;

      if (this.mode === 'resize') {
        this.updateResizePreview();
        this.updateResizeOverlay();
      } else if (this.mode === 'crop') {
        this.updateCropPreviewSize();
        this.updateCropOverlay();
      }
    });

    // Resize controls
    if (this.mode === 'resize') {
      this.setupResizeControls();
    }

    // Crop controls
    if (this.mode === 'crop') {
      this.setupCropControls();
    }
  }

  protected setupResizeControls(): void {
    const widthScrollbar = document.getElementById('widthScrollbar') as HTMLInputElement;
    const heightScrollbar = document.getElementById('heightScrollbar') as HTMLInputElement;
    const widthScrollbarValue = document.getElementById('widthScrollbarValue');
    const heightScrollbarValue = document.getElementById('heightScrollbarValue');
    const aspectLockCheckbox = document.getElementById('aspectRatioLock') as HTMLInputElement;

    // Width scrollbar handler
    widthScrollbar?.addEventListener('input', () => {
      const width = parseInt(widthScrollbar.value) || 0;
      this.newDimensions.width = width;

      if (this.aspectRatioLocked && width > 0) {
        const height = Math.round(width / this.aspectRatio);
        this.newDimensions.height = height;
        if (heightScrollbar) heightScrollbar.value = height.toString();
        if (heightScrollbarValue) heightScrollbarValue.textContent = height.toString();
      }

      if (widthScrollbarValue) widthScrollbarValue.textContent = width.toString();
      this.updateDimensionDisplay();
      this.updateResizePreview();
    });

    // Height scrollbar handler
    heightScrollbar?.addEventListener('input', () => {
      const height = parseInt(heightScrollbar.value) || 0;
      this.newDimensions.height = height;

      if (this.aspectRatioLocked && height > 0) {
        const width = Math.round(height * this.aspectRatio);
        this.newDimensions.width = width;
        if (widthScrollbar) widthScrollbar.value = width.toString();
        if (widthScrollbarValue) widthScrollbarValue.textContent = width.toString();
      }

      if (heightScrollbarValue) heightScrollbarValue.textContent = height.toString();
      this.updateDimensionDisplay();
      this.updateResizePreview();
    });

    // Aspect ratio lock checkbox handler
    aspectLockCheckbox?.addEventListener('change', () => {
      this.aspectRatioLocked = aspectLockCheckbox.checked;
      
      // If locking, update height to match current width
      if (this.aspectRatioLocked && this.newDimensions.width > 0) {
        const height = Math.round(this.newDimensions.width / this.aspectRatio);
        this.newDimensions.height = height;
        if (heightScrollbar) heightScrollbar.value = height.toString();
        if (heightScrollbarValue) heightScrollbarValue.textContent = height.toString();
        this.updateDimensionDisplay();
        this.updateResizePreview();
      }
    });
  }

  protected setupCropControls(): void {
    // Setup aspect ratio preset buttons
    const presetButtons = document.querySelectorAll('.preset-btn');
    presetButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        presetButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const ratio = btn.getAttribute('data-ratio');
        this.setAspectRatioPreset(ratio || 'free');
      });
    });
  }

  protected setAspectRatioPreset(ratio: string): void {
    // Will be implemented in CropManager
    console.log('Setting aspect ratio preset:', ratio);
  }

  protected handleFormatChange(format: string): void {
    this.selectedFormat = format;
    this.updateGenerateButton();
  }

  protected async handleFileUpload(file: File): Promise<void> {
    try {
      this.currentFile = file;

      // Load the image
      const img = await this.loadImage(file);
      this.currentImage = img;

      // Store original dimensions
      this.originalDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };

      // Initialize new dimensions (same as original)
      this.newDimensions = { ...this.originalDimensions };

      // Calculate aspect ratio
      this.aspectRatio = this.originalDimensions.width / this.originalDimensions.height;

      // Show the image in the editor
      const previewImage = document.getElementById('previewImage') as HTMLImageElement;
      const uploadSection = document.getElementById('uploadSection');
      const imageEditorSection = document.getElementById('imageEditorSection');
      const outputFormatSection = document.getElementById('outputFormatSection');
      const clearImageBtn = document.getElementById('clearImageBtn');

      if (previewImage && img.src) {
        // Set up onload handler before setting src to ensure dimensions are set after image loads
        previewImage.onload = () => {
          // For resize mode, set initial display to 80% of container to allow scaling up
          if (this.mode === 'resize') {
            const editorCanvas = document.getElementById('editorCanvas');
            if (editorCanvas) {
              const containerWidth = editorCanvas.clientWidth;
              const containerHeight = editorCanvas.clientHeight;

              // Calculate 80% size
              const targetWidth = containerWidth * 0.8;
              const targetHeight = containerHeight * 0.8;

              // Maintain aspect ratio
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              let displayWidth: number;
              let displayHeight: number;

              if (aspectRatio > (targetWidth / targetHeight)) {
                displayWidth = targetWidth;
                displayHeight = targetWidth / aspectRatio;
              } else {
                displayHeight = targetHeight;
                displayWidth = targetHeight * aspectRatio;
              }

              previewImage.style.width = `${displayWidth}px`;
              previewImage.style.height = `${displayHeight}px`;
              previewImage.style.maxWidth = 'none';
              previewImage.style.maxHeight = 'none';
              previewImage.style.objectFit = 'fill';

              console.log('Initial resize preview set to:', `${Math.round(displayWidth)}x${Math.round(displayHeight)}`);
            }
          } else {
            // For crop mode, size image to fit the canvas (including small images)
            this.updateCropPreviewSize();
          }
        };

        previewImage.src = img.src;
      }

      // Hide upload section, show editor
      if (uploadSection) uploadSection.style.display = 'none';
      if (imageEditorSection) imageEditorSection.style.display = 'flex';
      if (outputFormatSection) outputFormatSection.style.display = 'flex';
      if (clearImageBtn) clearImageBtn.style.display = 'inline-flex';

      // Initialize mode-specific elements
      if (this.mode === 'resize') {
        this.initResizeMode();
      } else if (this.mode === 'crop') {
        this.initCropMode();
      }

      // Update dimension display
      this.updateDimensionDisplay();

      // Enable generate button
      this.updateGenerateButton();

    } catch (error) {
      console.error('Error loading image:', error);
      alert('Error loading image. Please try again.');
    }
  }

  protected initResizeMode(): void {
    const widthScrollbar = document.getElementById('widthScrollbar') as HTMLInputElement;
    const heightScrollbar = document.getElementById('heightScrollbar') as HTMLInputElement;
    const widthScrollbarValue = document.getElementById('widthScrollbarValue');
    const heightScrollbarValue = document.getElementById('heightScrollbarValue');
    const resizeScrollbars = document.getElementById('resizeScrollbars');
    const aspectRatioControl = document.getElementById('aspectRatioControl');
    const aspectLockCheckbox = document.getElementById('aspectRatioLock') as HTMLInputElement;
    
    // Set scrollbar values and ranges
    if (widthScrollbar) {
      widthScrollbar.min = '1';
      widthScrollbar.max = (this.originalDimensions.width * 3).toString(); // Allow up to 3x original
      widthScrollbar.value = this.originalDimensions.width.toString();
    }
    if (heightScrollbar) {
      heightScrollbar.min = '1';
      heightScrollbar.max = (this.originalDimensions.height * 3).toString();
      heightScrollbar.value = this.originalDimensions.height.toString();
    }
    if (widthScrollbarValue) widthScrollbarValue.textContent = this.originalDimensions.width.toString();
    if (heightScrollbarValue) heightScrollbarValue.textContent = this.originalDimensions.height.toString();
    
    // Show scrollbars and aspect ratio control
    if (resizeScrollbars) resizeScrollbars.style.display = 'flex';
    if (aspectRatioControl) aspectRatioControl.style.display = 'flex';
    
    // Set checkbox to checked (locked by default)
    if (aspectLockCheckbox) aspectLockCheckbox.checked = true;

    // Set initial dimensions to original
    this.newDimensions = { ...this.originalDimensions };

    // Initialize resize overlay
    this.initResizeOverlay();
  }

  protected initCropMode(): void {
    // Initialize crop area to full image
    this.cropArea = {
      x: 0,
      y: 0,
      width: this.originalDimensions.width,
      height: this.originalDimensions.height
    };

    // Will be further implemented in CropManager
    this.initCropOverlay();
  }

  protected initCropOverlay(): void {
    // Initialize the crop overlay to match image dimensions
    const cropOverlay = document.getElementById('cropOverlay');
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    const editorCanvas = document.getElementById('editorCanvas');

    if (cropOverlay && previewImage && editorCanvas) {
      // Ensure canvas has relative positioning for absolute overlay
      editorCanvas.style.position = 'relative';

      // Set overlay styles
      cropOverlay.style.display = 'block';
      cropOverlay.style.position = 'absolute';
      this.setupCropDragHandlers();

      // Function to update overlay with retry logic
      const updateWithRetry = (attempts = 0) => {
        if (attempts > 20) {
          console.error('Failed to initialize crop overlay after multiple attempts');
          console.log('Debug info:', {
            previewComplete: previewImage.complete,
            naturalWidth: previewImage.naturalWidth,
            naturalHeight: previewImage.naturalHeight,
            rectWidth: previewImage.getBoundingClientRect().width,
            rectHeight: previewImage.getBoundingClientRect().height
          });
          return;
        }

        // Check if image has both natural dimensions and rendered dimensions
        const rect = previewImage.getBoundingClientRect();
        const hasNaturalDimensions = previewImage.naturalWidth > 0 && previewImage.naturalHeight > 0;
        const hasRenderedDimensions = rect.width > 0 && rect.height > 0;

        if (hasNaturalDimensions && hasRenderedDimensions) {
          console.log(`[initCropOverlay] Updating overlay after ${attempts} attempts`);
          this.updateCropOverlay();
        } else {
          // Retry after a short delay
          setTimeout(() => updateWithRetry(attempts + 1), 100);
        }
      };

      // Always use retry logic with delays to ensure proper layout
      setTimeout(() => updateWithRetry(0), 0);

      // Update preview size and overlay on window resize
      const resizeHandler = () => {
        this.updateCropPreviewSize();
        requestAnimationFrame(() => this.updateCropOverlay());
      };
      window.removeEventListener('resize', resizeHandler); // Remove any existing listeners
      window.addEventListener('resize', resizeHandler);
    }
  }

  protected setupCropDragHandlers(): void {
    const cropOverlay = document.getElementById('cropOverlay');
    const editorCanvas = document.getElementById('editorCanvas');

    if (!cropOverlay || !editorCanvas) return;

    let isDragging = false;
    let isResizing = false;
    let activeHandle: string | null = null;
    let pointerActive = false;
    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startCrop = { ...this.cropArea };
    let rafId: number | null = null;
    let lastClientX = 0;
    let lastClientY = 0;

    const handles = cropOverlay.querySelectorAll('.crop-handle');

    const startResize = (clientX: number, clientY: number, handle: string | null) => {
      if (!handle) return;
      isResizing = true;
      activeHandle = handle;
      startX = clientX;
      startY = clientY;
      lastClientX = clientX;
      lastClientY = clientY;
      startCrop = { ...this.cropArea };
    };

    const startDrag = (clientX: number, clientY: number) => {
      isDragging = true;
      startX = clientX;
      startY = clientY;
      lastClientX = clientX;
      lastClientY = clientY;
      startCrop = { ...this.cropArea };
    };

    const performUpdate = () => {
      if (!isDragging && !isResizing) {
        rafId = null;
        return;
      }

      const dx = lastClientX - startX;
      const dy = lastClientY - startY;

      if (isResizing && activeHandle) {
        this.handleCropResize(activeHandle, dx, dy, startCrop);
      } else if (isDragging) {
        this.handleCropMove(dx, dy, startCrop);
      }

      this.updateCropOverlay();
      this.updateDimensionDisplay();

      rafId = null;
    };

    const updateDrag = (clientX: number, clientY: number) => {
      if (!isDragging && !isResizing) return;

      lastClientX = clientX;
      lastClientY = clientY;

      // Use RAF to throttle updates to 60fps max
      if (rafId === null) {
        rafId = requestAnimationFrame(performUpdate);
      }
    };

    const endDrag = () => {
      if (isDragging || isResizing) {
        // Perform final update
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        performUpdate();
      }

      isDragging = false;
      isResizing = false;
      activeHandle = null;
      pointerActive = false;
      activePointerId = null;
    };

    // Handle resize via corner/edge handles
    handles.forEach(handle => {
      handle.addEventListener('mousedown', (e: Event) => {
        if (pointerActive) return;
        e.stopPropagation();
        const mouseEvent = e as MouseEvent;
        startResize(mouseEvent.clientX, mouseEvent.clientY, (handle as HTMLElement).dataset.handle || null);
      });

      handle.addEventListener('touchstart', (e: Event) => {
        const touchEvent = e as TouchEvent;
        const touch = touchEvent.touches[0];
        if (!touch) return;
        touchEvent.preventDefault();
        touchEvent.stopPropagation();
        pointerActive = true;
        activePointerId = null;
        startResize(touch.clientX, touch.clientY, (handle as HTMLElement).dataset.handle || null);
      }, { passive: false });

      handle.addEventListener('pointerdown', (e: Event) => {
        const pointerEvent = e as PointerEvent;
        pointerEvent.preventDefault();
        pointerEvent.stopPropagation();
        pointerActive = true;
        activePointerId = pointerEvent.pointerId;
        (handle as HTMLElement).setPointerCapture?.(pointerEvent.pointerId);
        startResize(pointerEvent.clientX, pointerEvent.clientY, (handle as HTMLElement).dataset.handle || null);
      });
    });

    // Handle move via overlay drag
    cropOverlay.addEventListener('mousedown', (e: MouseEvent) => {
      if (pointerActive) return;
      if (isResizing) return;
      startDrag(e.clientX, e.clientY);
    });

    cropOverlay.addEventListener('touchstart', (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (isResizing) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      touchEvent.preventDefault();
      pointerActive = true;
      activePointerId = null;
      startDrag(touch.clientX, touch.clientY);
    }, { passive: false });

    cropOverlay.addEventListener('pointerdown', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      pointerEvent.preventDefault();
      if (isResizing) return;
      pointerActive = true;
      activePointerId = pointerEvent.pointerId;
      cropOverlay.setPointerCapture?.(pointerEvent.pointerId);
      startDrag(pointerEvent.clientX, pointerEvent.clientY);
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (pointerActive) return;
      updateDrag(e.clientX, e.clientY);
    });

    document.addEventListener('pointermove', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (!pointerActive || (activePointerId !== null && pointerEvent.pointerId !== activePointerId)) return;
      updateDrag(pointerEvent.clientX, pointerEvent.clientY);
    });

    document.addEventListener('touchmove', (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!isDragging && !isResizing) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      touchEvent.preventDefault();
      updateDrag(touch.clientX, touch.clientY);
    }, { passive: false });

    document.addEventListener('mouseup', () => {
      endDrag();
    });

    document.addEventListener('pointerup', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (pointerActive && activePointerId !== null && pointerEvent.pointerId !== activePointerId) return;
      endDrag();
    });

    document.addEventListener('touchend', () => {
      endDrag();
    });

    document.addEventListener('touchcancel', () => {
      endDrag();
    });

    document.addEventListener('pointercancel', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (pointerActive && activePointerId !== null && pointerEvent.pointerId !== activePointerId) return;
      endDrag();
    });
  }

  protected handleCropResize(handle: string, dx: number, dy: number, startCrop: CropArea): void {
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    if (!previewImage) return;

    const imageRect = previewImage.getBoundingClientRect();
    const scaleX = this.originalDimensions.width / imageRect.width;
    const scaleY = this.originalDimensions.height / imageRect.height;

    const scaledDx = dx * scaleX;
    const scaledDy = dy * scaleY;

    let newCrop = { ...startCrop };

    // Check if aspect ratio should be locked (crop mode with preset selected)
    const cropTool = this as any; // Access ImageCropTool properties
    const isAspectLocked = cropTool.currentAspectRatioPreset && cropTool.currentAspectRatioPreset !== 'free';
    let targetRatio = 1;
    
    if (isAspectLocked) {
      // Parse aspect ratio from preset (e.g., "4:3" -> 1.333)
      const parts = cropTool.currentAspectRatioPreset.split(':');
      if (parts.length === 2) {
        targetRatio = parseInt(parts[0]) / parseInt(parts[1]);
      }
    }

    switch (handle) {
      case 'nw':
        newCrop.x = Math.max(0, startCrop.x + scaledDx);
        newCrop.y = Math.max(0, startCrop.y + scaledDy);
        newCrop.width = startCrop.width - scaledDx;
        newCrop.height = startCrop.height - scaledDy;
        
        if (isAspectLocked) {
          // For corner handles, use the dimension with larger change as primary
          const widthChangeRatio = Math.abs((newCrop.width - startCrop.width) / startCrop.width);
          const heightChangeRatio = Math.abs((newCrop.height - startCrop.height) / startCrop.height);
          
          if (widthChangeRatio > heightChangeRatio) {
            // Width changed more - use it as primary
            newCrop.height = newCrop.width / targetRatio;
            newCrop.y = startCrop.y + startCrop.height - newCrop.height;
          } else {
            // Height changed more - use it as primary
            newCrop.width = newCrop.height * targetRatio;
            newCrop.x = startCrop.x + startCrop.width - newCrop.width;
            newCrop.y = startCrop.y + startCrop.height - newCrop.height;
          }
        }
        break;
      case 'ne':
        newCrop.y = Math.max(0, startCrop.y + scaledDy);
        newCrop.width = startCrop.width + scaledDx;
        newCrop.height = startCrop.height - scaledDy;
        
        if (isAspectLocked) {
          // For corner handles, use the dimension with larger change as primary
          const widthChangeRatio = Math.abs((newCrop.width - startCrop.width) / startCrop.width);
          const heightChangeRatio = Math.abs((newCrop.height - startCrop.height) / startCrop.height);
          
          if (widthChangeRatio > heightChangeRatio) {
            // Width changed more - use it as primary
            newCrop.height = newCrop.width / targetRatio;
            newCrop.y = startCrop.y + startCrop.height - newCrop.height;
          } else {
            // Height changed more - use it as primary
            newCrop.width = newCrop.height * targetRatio;
            newCrop.y = startCrop.y + startCrop.height - newCrop.height;
          }
        }
        break;
      case 'sw':
        newCrop.x = Math.max(0, startCrop.x + scaledDx);
        newCrop.width = startCrop.width - scaledDx;
        newCrop.height = startCrop.height + scaledDy;
        
        if (isAspectLocked) {
          // For corner handles, use the dimension with larger change as primary
          const widthChangeRatio = Math.abs((newCrop.width - startCrop.width) / startCrop.width);
          const heightChangeRatio = Math.abs((newCrop.height - startCrop.height) / startCrop.height);
          
          if (widthChangeRatio > heightChangeRatio) {
            // Width changed more - use it as primary
            newCrop.height = newCrop.width / targetRatio;
          } else {
            // Height changed more - use it as primary
            newCrop.width = newCrop.height * targetRatio;
            newCrop.x = startCrop.x + startCrop.width - newCrop.width;
          }
        }
        break;
      case 'se':
        newCrop.width = startCrop.width + scaledDx;
        newCrop.height = startCrop.height + scaledDy;
        
        if (isAspectLocked) {
          // For corner handles, use the dimension with larger change as primary
          const widthChangeRatio = Math.abs((newCrop.width - startCrop.width) / startCrop.width);
          const heightChangeRatio = Math.abs((newCrop.height - startCrop.height) / startCrop.height);
          
          if (widthChangeRatio > heightChangeRatio) {
            // Width changed more - use it as primary
            newCrop.height = newCrop.width / targetRatio;
          } else {
            // Height changed more - use it as primary
            newCrop.width = newCrop.height * targetRatio;
          }
        }
        break;
      case 'n':
        newCrop.y = Math.max(0, startCrop.y + scaledDy);
        newCrop.height = startCrop.height - scaledDy;
        
        if (isAspectLocked) {
          // Use height change as primary, adjust width
          newCrop.width = newCrop.height * targetRatio;
        }
        break;
      case 's':
        newCrop.height = startCrop.height + scaledDy;
        
        if (isAspectLocked) {
          // Use height change as primary, adjust width
          newCrop.width = newCrop.height * targetRatio;
        }
        break;
      case 'w':
        newCrop.x = Math.max(0, startCrop.x + scaledDx);
        newCrop.width = startCrop.width - scaledDx;
        
        if (isAspectLocked) {
          // Use width change as primary, adjust height
          newCrop.height = newCrop.width / targetRatio;
        }
        break;
      case 'e':
        newCrop.width = startCrop.width + scaledDx;
        
        if (isAspectLocked) {
          // Use width change as primary, adjust height
          newCrop.height = newCrop.width / targetRatio;
        }
        break;
    }

    // Clamp values
    const preclampWidth = newCrop.width;
    const preclampHeight = newCrop.height;
    newCrop.width = Math.max(10, Math.min(newCrop.width, this.originalDimensions.width - newCrop.x));
    newCrop.height = Math.max(10, Math.min(newCrop.height, this.originalDimensions.height - newCrop.y));

    // If aspect ratio is locked and clamping changed dimensions, re-adjust to maintain ratio
    if (isAspectLocked && (newCrop.width !== preclampWidth || newCrop.height !== preclampHeight)) {
      // Clamping occurred - determine which dimension was more constrained
      const widthConstrained = newCrop.width < preclampWidth;
      const heightConstrained = newCrop.height < preclampHeight;
      
      if (widthConstrained && !heightConstrained) {
        // Width was limited, adjust height to match
        newCrop.height = newCrop.width / targetRatio;
        newCrop.height = Math.max(10, Math.min(newCrop.height, this.originalDimensions.height - newCrop.y));
      } else if (heightConstrained && !widthConstrained) {
        // Height was limited, adjust width to match
        newCrop.width = newCrop.height * targetRatio;
        newCrop.width = Math.max(10, Math.min(newCrop.width, this.originalDimensions.width - newCrop.x));
      } else if (widthConstrained && heightConstrained) {
        // Both were constrained - use the more constrained one as primary
        const widthRatio = newCrop.width / preclampWidth;
        const heightRatio = newCrop.height / preclampHeight;
        
        if (widthRatio < heightRatio) {
          // Width more constrained, use it as primary
          newCrop.height = newCrop.width / targetRatio;
          newCrop.height = Math.max(10, Math.min(newCrop.height, this.originalDimensions.height - newCrop.y));
        } else {
          // Height more constrained, use it as primary
          newCrop.width = newCrop.height * targetRatio;
          newCrop.width = Math.max(10, Math.min(newCrop.width, this.originalDimensions.width - newCrop.x));
        }
      }
    }

    this.cropArea = newCrop;
    this.newDimensions = { width: Math.round(newCrop.width), height: Math.round(newCrop.height) };
  }

  protected handleCropMove(dx: number, dy: number, startCrop: CropArea): void {
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    if (!previewImage) return;

    const imageRect = previewImage.getBoundingClientRect();
    const scaleX = this.originalDimensions.width / imageRect.width;
    const scaleY = this.originalDimensions.height / imageRect.height;

    let newX = startCrop.x + dx * scaleX;
    let newY = startCrop.y + dy * scaleY;

    // Clamp to image bounds
    newX = Math.max(0, Math.min(newX, this.originalDimensions.width - this.cropArea.width));
    newY = Math.max(0, Math.min(newY, this.originalDimensions.height - this.cropArea.height));

    this.cropArea.x = newX;
    this.cropArea.y = newY;
  }

  protected updateCropOverlay(): void {
    const cropOverlay = document.getElementById('cropOverlay');
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;

    if (!cropOverlay || !previewImage) return;

    // Ensure position is absolute
    cropOverlay.style.position = 'absolute';

    const imageRect = previewImage.getBoundingClientRect();
    const containerRect = previewImage.parentElement?.getBoundingClientRect();

    if (!containerRect) return;

    const scaleX = imageRect.width / this.originalDimensions.width;
    const scaleY = imageRect.height / this.originalDimensions.height;

    const offsetX = imageRect.left - containerRect.left;
    const offsetY = imageRect.top - containerRect.top;

    cropOverlay.style.left = `${offsetX + this.cropArea.x * scaleX}px`;
    cropOverlay.style.top = `${offsetY + this.cropArea.y * scaleY}px`;
    cropOverlay.style.width = `${this.cropArea.width * scaleX}px`;
    cropOverlay.style.height = `${this.cropArea.height * scaleY}px`;
  }

  protected initResizeOverlay(): void {
    const resizeOverlay = document.getElementById('resizeOverlay');
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    const editorCanvas = document.getElementById('editorCanvas');

    if (resizeOverlay && previewImage && editorCanvas) {
      resizeOverlay.style.display = 'block';
      resizeOverlay.style.position = 'absolute';
      this.setupResizeDragHandlers();

      // Update overlay with retry logic
      const updateWithRetry = (attempts = 0) => {
        if (attempts > 20) return;

        const rect = previewImage.getBoundingClientRect();
        if (previewImage.naturalWidth > 0 && rect.width > 0) {
          this.updateResizeOverlay();
        } else {
          setTimeout(() => updateWithRetry(attempts + 1), 100);
        }
      };

      setTimeout(() => updateWithRetry(0), 0);

      // Update overlay on window resize
      const resizeHandler = () => this.updateResizeOverlay();
      window.removeEventListener('resize', resizeHandler);
      window.addEventListener('resize', resizeHandler);
    }
  }

  protected setupResizeDragHandlers(): void {
    const resizeOverlay = document.getElementById('resizeOverlay');
    if (!resizeOverlay) return;

    let isResizing = false;
    let activeHandle: string | null = null;
    let pointerActive = false;
    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startDimensions = { ...this.newDimensions };
    let rafId: number | null = null;
    let lastClientX = 0;
    let lastClientY = 0;

    const startResize = (clientX: number, clientY: number, handle: string | null) => {
      if (!handle) return;
      isResizing = true;
      activeHandle = handle;
      startX = clientX;
      startY = clientY;
      lastClientX = clientX;
      lastClientY = clientY;
      startDimensions = { ...this.newDimensions };
    };

    const performUpdate = () => {
      if (!isResizing || !activeHandle) {
        rafId = null;
        return;
      }

      const dx = lastClientX - startX;
      const dy = lastClientY - startY;

      this.handleResizeOverlayResize(activeHandle, dx, dy, startDimensions);
      this.updateResizePreview();
      this.updateDimensionDisplay();
      this.updateResizeOverlay();

      rafId = null;
    };

    const updateResize = (clientX: number, clientY: number) => {
      if (!isResizing || !activeHandle) return;

      lastClientX = clientX;
      lastClientY = clientY;

      // Use RAF to throttle updates to 60fps max
      if (rafId === null) {
        rafId = requestAnimationFrame(performUpdate);
      }
    };

    const endResize = () => {
      if (isResizing) {
        // Perform final update
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        performUpdate();

        // Update input fields once at the end
        const widthInput = document.getElementById('widthInput') as HTMLInputElement;
        const heightInput = document.getElementById('heightInput') as HTMLInputElement;
        if (widthInput) widthInput.value = Math.round(this.newDimensions.width).toString();
        if (heightInput) heightInput.value = Math.round(this.newDimensions.height).toString();
      }

      isResizing = false;
      activeHandle = null;
      pointerActive = false;
      activePointerId = null;
    };

    const handles = resizeOverlay.querySelectorAll('.resize-handle');

    handles.forEach(handle => {
      handle.addEventListener('mousedown', (e: Event) => {
        if (pointerActive) return;
        e.stopPropagation();
        e.preventDefault();
        const mouseEvent = e as MouseEvent;
        startResize(mouseEvent.clientX, mouseEvent.clientY, (handle as HTMLElement).dataset.handle || null);
      });

      handle.addEventListener('touchstart', (e: Event) => {
        const touchEvent = e as TouchEvent;
        const touch = touchEvent.touches[0];
        if (!touch) return;
        touchEvent.stopPropagation();
        touchEvent.preventDefault();
        pointerActive = true;
        activePointerId = null;
        startResize(touch.clientX, touch.clientY, (handle as HTMLElement).dataset.handle || null);
      }, { passive: false });

      handle.addEventListener('pointerdown', (e: Event) => {
        const pointerEvent = e as PointerEvent;
        pointerEvent.stopPropagation();
        pointerEvent.preventDefault();
        pointerActive = true;
        activePointerId = pointerEvent.pointerId;
        (handle as HTMLElement).setPointerCapture?.(pointerEvent.pointerId);
        startResize(pointerEvent.clientX, pointerEvent.clientY, (handle as HTMLElement).dataset.handle || null);
      });
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (pointerActive) return;
      updateResize(e.clientX, e.clientY);
    });

    document.addEventListener('pointermove', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (!pointerActive || (activePointerId !== null && pointerEvent.pointerId !== activePointerId)) return;
      updateResize(pointerEvent.clientX, pointerEvent.clientY);
    });

    document.addEventListener('touchmove', (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!isResizing) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      touchEvent.preventDefault();
      updateResize(touch.clientX, touch.clientY);
    }, { passive: false });

    document.addEventListener('mouseup', () => {
      endResize();
    });

    document.addEventListener('pointerup', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (pointerActive && activePointerId !== null && pointerEvent.pointerId !== activePointerId) return;
      endResize();
    });

    document.addEventListener('touchend', () => {
      endResize();
    });

    document.addEventListener('touchcancel', () => {
      endResize();
    });

    document.addEventListener('pointercancel', (e: Event) => {
      const pointerEvent = e as PointerEvent;
      if (pointerActive && activePointerId !== null && pointerEvent.pointerId !== activePointerId) return;
      endResize();
    });
  }

  protected handleResizeOverlayResize(handle: string, dx: number, dy: number, startDimensions: ImageDimensions): void {
    // Apply sensitivity multiplier for better UX
    // 2x multiplier makes dragging more responsive, especially on mobile
    const sensitivity = 2.0;
    dx = dx * sensitivity;
    dy = dy * sensitivity;

    let newWidth = startDimensions.width;
    let newHeight = startDimensions.height;

    switch (handle) {
      case 'se':
        newWidth = startDimensions.width + dx;
        newHeight = startDimensions.height + dy;
        break;
      case 'sw':
        newWidth = startDimensions.width - dx;
        newHeight = startDimensions.height + dy;
        break;
      case 'ne':
        newWidth = startDimensions.width + dx;
        newHeight = startDimensions.height - dy;
        break;
      case 'nw':
        newWidth = startDimensions.width - dx;
        newHeight = startDimensions.height - dy;
        break;
      case 'n':
        // Only adjust height (top edge)
        newWidth = startDimensions.width;
        newHeight = startDimensions.height - dy;
        break;
      case 's':
        // Only adjust height (bottom edge)
        newWidth = startDimensions.width;
        newHeight = startDimensions.height + dy;
        break;
      case 'e':
        // Only adjust width (right edge)
        newWidth = startDimensions.width + dx;
        newHeight = startDimensions.height;
        break;
      case 'w':
        // Only adjust width (left edge)
        newWidth = startDimensions.width - dx;
        newHeight = startDimensions.height;
        break;
    }

    // Apply aspect ratio lock if enabled
    if (this.aspectRatioLocked) {
      // For edge handles, determine which dimension was changed and adjust the other
      const isVerticalEdge = ['n', 's'].includes(handle);
      const isHorizontalEdge = ['e', 'w'].includes(handle);

      if (isVerticalEdge) {
        // Height was changed, adjust width to maintain ratio
        newWidth = newHeight * this.aspectRatio;
      } else if (isHorizontalEdge) {
        // Width was changed, adjust height to maintain ratio
        newHeight = newWidth / this.aspectRatio;
      } else {
        // Corner handles - use the dominant direction
        if (Math.abs(dx) > Math.abs(dy)) {
          newHeight = newWidth / this.aspectRatio;
        } else {
          newWidth = newHeight * this.aspectRatio;
        }
      }
    }

    // Clamp values to reasonable limits and round to integers
    newWidth = Math.round(Math.max(10, Math.min(newWidth, this.originalDimensions.width * 10)));
    newHeight = Math.round(Math.max(10, Math.min(newHeight, this.originalDimensions.height * 10)));

    this.newDimensions = { width: newWidth, height: newHeight };
  }

  protected updateResizeOverlay(): void {
    const resizeOverlay = document.getElementById('resizeOverlay');
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;

    if (!resizeOverlay || !previewImage) return;

    resizeOverlay.style.position = 'absolute';

    const imageRect = previewImage.getBoundingClientRect();
    const containerRect = previewImage.parentElement?.getBoundingClientRect();

    if (!containerRect) return;

    // Purple frame ALWAYS represents the original image size
    // It should maintain the original aspect ratio regardless of whether aspect ratio is locked
    // When aspect ratio is locked, both width and height scale together
    // When aspect ratio is unlocked, we need to use the appropriate scale factor
    
    let scaleFactor: number;
    let overlayWidth: number;
    let overlayHeight: number;
    
    if (this.aspectRatioLocked) {
      // When locked, both dimensions scale together
      scaleFactor = this.newDimensions.width / this.originalDimensions.width;
      const effectiveScaleFactor = scaleFactor / this.displayScaleFactor;
      overlayWidth = imageRect.width / effectiveScaleFactor;
      overlayHeight = imageRect.height / effectiveScaleFactor;
    } else {
      // When unlocked, width and height can change independently
      // Purple frame should show original size, so we calculate based on original aspect ratio
      const scaleFactorX = this.newDimensions.width / this.originalDimensions.width;
      const scaleFactorY = this.newDimensions.height / this.originalDimensions.height;
      
      // Use the average to determine overall scale for display
      const avgScaleFactor = (scaleFactorX + scaleFactorY) / 2;
      
      // Calculate overlay size that maintains original aspect ratio
      const originalAspectRatio = this.originalDimensions.width / this.originalDimensions.height;
      
      // Start with imageRect proportions but adjust to show original ratio
      const imageAspectRatio = imageRect.width / imageRect.height;
      
      // The overlay should fit within the preview and maintain original aspect ratio
      if (originalAspectRatio > imageAspectRatio) {
        // Original is wider - fit to width
        overlayWidth = imageRect.width / (avgScaleFactor / this.displayScaleFactor);
        overlayHeight = overlayWidth / originalAspectRatio;
      } else {
        // Original is taller - fit to height
        overlayHeight = imageRect.height / (avgScaleFactor / this.displayScaleFactor);
        overlayWidth = overlayHeight * originalAspectRatio;
      }
    }
    
    // Center the overlay within the image
    const imageOffsetX = imageRect.left - containerRect.left;
    const imageOffsetY = imageRect.top - containerRect.top;
    const overlayCenterX = imageOffsetX + (imageRect.width - overlayWidth) / 2;
    const overlayCenterY = imageOffsetY + (imageRect.height - overlayHeight) / 2;

    // Position and size the overlay to show the reference frame
    resizeOverlay.style.left = `${overlayCenterX}px`;
    resizeOverlay.style.top = `${overlayCenterY}px`;
    resizeOverlay.style.width = `${overlayWidth}px`;
    resizeOverlay.style.height = `${overlayHeight}px`;
    resizeOverlay.style.border = '2px solid #8B5CF6';
    resizeOverlay.style.pointerEvents = 'none';
  }

  protected updateDimensionDisplay(): void {
    const originalDimensionsEl = document.getElementById('originalDimensions');
    const newDimensionsEl = document.getElementById('newDimensions');

    if (originalDimensionsEl) {
      originalDimensionsEl.textContent = `${Math.round(this.originalDimensions.width)} × ${Math.round(this.originalDimensions.height)} px`;
    }

    if (newDimensionsEl) {
      if (this.mode === 'crop') {
        newDimensionsEl.textContent = `${Math.round(this.cropArea.width)} × ${Math.round(this.cropArea.height)} px`;
      } else {
        newDimensionsEl.textContent = `${Math.round(this.newDimensions.width)} × ${Math.round(this.newDimensions.height)} px`;
      }
    }
  }

  protected updateResizePreview(): void {
    if (this.mode !== 'resize' || !this.currentImage) return;

    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    const editorCanvas = document.getElementById('editorCanvas');
    if (!previewImage || !editorCanvas) return;

    // Only update if we have valid dimensions
    if (this.newDimensions.width <= 0 || this.newDimensions.height <= 0) return;

    // Get the container dimensions
    const containerWidth = editorCanvas.clientWidth;
    const containerHeight = editorCanvas.clientHeight;

    // Calculate the scale factor: how the new dimensions relate to original dimensions
    const scaleFactorX = this.newDimensions.width / this.originalDimensions.width;
    const scaleFactorY = this.newDimensions.height / this.originalDimensions.height;

    // Use the average scale factor (they should be the same due to aspect ratio lock)
    const scaleFactor = (scaleFactorX + scaleFactorY) / 2;

    // Calculate what the display size should be based on scale factor
    // Start with 80% of container as the "1x" baseline
    const baseDisplayWidth = containerWidth * 0.8;
    const baseDisplayHeight = containerHeight * 0.8;

    // Calculate display dimensions with aspect ratio
    const aspectRatio = this.newDimensions.width / this.newDimensions.height;
    let targetDisplayWidth: number;
    let targetDisplayHeight: number;

    if (aspectRatio > (baseDisplayWidth / baseDisplayHeight)) {
      targetDisplayWidth = baseDisplayWidth * scaleFactor;
      targetDisplayHeight = targetDisplayWidth / aspectRatio;
    } else {
      targetDisplayHeight = baseDisplayHeight * scaleFactor;
      targetDisplayWidth = targetDisplayHeight * aspectRatio;
    }

    // Check if either dimension would exceed container bounds
    const wouldExceedWidth = targetDisplayWidth > containerWidth;
    const wouldExceedHeight = targetDisplayHeight > containerHeight;
    
    let displayWidth = targetDisplayWidth;
    let displayHeight = targetDisplayHeight;

    // If either dimension exceeds, clamp both proportionally
    // AND stop further scaling - the purple frame will continue to shrink
    if (wouldExceedWidth || wouldExceedHeight) {
      // Find the limiting factor
      const widthRatio = containerWidth / targetDisplayWidth;
      const heightRatio = containerHeight / targetDisplayHeight;
      const limitingRatio = Math.min(widthRatio, heightRatio);
      
      // Apply the limiting ratio to both dimensions to keep them at container boundary
      displayWidth = targetDisplayWidth * limitingRatio;
      displayHeight = targetDisplayHeight * limitingRatio;
      
      // Store the effective scale factor that's actually being applied
      // This will be used to make the purple frame proportionally smaller
      this.displayScaleFactor = limitingRatio;
    } else {
      // No boundary hit, display scale factor is 1:1 with calculated scale
      this.displayScaleFactor = 1;
    }

    // Apply the calculated dimensions
    previewImage.style.width = `${displayWidth}px`;
    previewImage.style.height = `${displayHeight}px`;
    previewImage.style.maxWidth = 'none';
    previewImage.style.maxHeight = 'none';
    previewImage.style.objectFit = 'fill';
    
    // Update the overlay to show the purple frame at original size
    this.updateResizeOverlay();
  }

  protected updateCropPreviewSize(): void {
    if (this.mode !== 'crop' || !this.currentImage) return;

    const previewImage = document.getElementById('previewImage') as HTMLImageElement;
    const editorCanvas = document.getElementById('editorCanvas');
    if (!previewImage || !editorCanvas) return;

    const containerWidth = editorCanvas.clientWidth;
    const containerHeight = editorCanvas.clientHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;

    // Fit the image within the canvas while preserving aspect ratio
    const aspectRatio = this.currentImage.naturalWidth / this.currentImage.naturalHeight;
    let displayWidth: number;
    let displayHeight: number;

    if (aspectRatio > (containerWidth / containerHeight)) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / aspectRatio;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * aspectRatio;
    }

    previewImage.style.width = `${displayWidth}px`;
    previewImage.style.height = `${displayHeight}px`;
    previewImage.style.maxWidth = 'none';
    previewImage.style.maxHeight = 'none';
    previewImage.style.objectFit = 'fill';
  }

  protected updateGenerateButton(): void {
    const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
    if (generateBtn) {
      generateBtn.disabled = !this.currentImage;
    }
  }

  protected clearImage(): void {
    this.currentFile = null;
    this.currentImage = null;
    this.originalDimensions = { width: 0, height: 0 };
    this.newDimensions = { width: 0, height: 0 };
    this.cropArea = { x: 0, y: 0, width: 0, height: 0 };
    
    // Reset aspect ratio lock to default (locked)
    this.aspectRatioLocked = true;
    const aspectLockCheckbox = document.getElementById('aspectRatioLock') as HTMLInputElement;
    if (aspectLockCheckbox) {
      aspectLockCheckbox.checked = true;
    }

    // Reset UI
    const uploadSection = document.getElementById('uploadSection');
    const imageEditorSection = document.getElementById('imageEditorSection');
    const outputFormatSection = document.getElementById('outputFormatSection');
    const clearImageBtn = document.getElementById('clearImageBtn');
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    const previewImage = document.getElementById('previewImage') as HTMLImageElement;

    if (uploadSection) uploadSection.style.display = 'flex';
    if (imageEditorSection) imageEditorSection.style.display = 'none';
    if (outputFormatSection) outputFormatSection.style.display = 'none';
    if (clearImageBtn) clearImageBtn.style.display = 'none';
    if (fileInput) fileInput.value = '';

    // Reset preview image styles
    if (previewImage) {
      previewImage.style.width = '';
      previewImage.style.height = '';
      previewImage.style.maxWidth = '';
      previewImage.style.maxHeight = '';
      previewImage.style.objectFit = '';
    }

    this.updateGenerateButton();
  }

  protected loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  protected async processAndDownload(): Promise<void> {
    if (!this.currentImage || !this.currentFile) return;

    try {
      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      const btnText = document.getElementById('generateBtnText');
      const originalText = btnText?.textContent;

      if (generateBtn) generateBtn.disabled = true;
      if (btnText) btnText.textContent = 'Processing...';

      // Create canvas with new dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) throw new Error('Failed to get canvas context');

      if (this.mode === 'resize') {
        // Resize mode: scale the entire image
        canvas.width = this.newDimensions.width;
        canvas.height = this.newDimensions.height;
        ctx.drawImage(this.currentImage, 0, 0, this.newDimensions.width, this.newDimensions.height);
      } else {
        // Crop mode: extract the crop area
        canvas.width = Math.round(this.cropArea.width);
        canvas.height = Math.round(this.cropArea.height);
        ctx.drawImage(
          this.currentImage,
          this.cropArea.x, this.cropArea.y,
          this.cropArea.width, this.cropArea.height,
          0, 0,
          canvas.width, canvas.height
        );
      }

      // Convert to blob
      const mimeType = this.getMimeType(this.selectedFormat);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Failed to create blob')),
          mimeType,
          0.92
        );
      });

      // Download
      this.downloadImage(blob);

      // Show thank you modal
      this.modal.showThankYou();

      // Reset button
      if (generateBtn) generateBtn.disabled = false;
      if (btnText && originalText) btnText.textContent = originalText;

    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error processing image. Please try again.');

      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      const btnText = document.getElementById('generateBtnText');
      if (generateBtn) generateBtn.disabled = false;
      if (btnText) btnText.textContent = this.mode === 'crop' ? 'Crop & Download' : 'Resize & Download';
    }
  }

  protected getMimeType(format: string): string {
    const mimeTypes: Record<string, string> = {
      'png': 'image/png',
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpeg',
      'webp': 'image/webp'
    };
    return mimeTypes[format.toLowerCase()] || 'image/png';
  }

  protected downloadImage(blob: Blob): void {
    if (!this.currentFile) return;

    const originalName = this.currentFile.name;
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
    const suffix = this.mode === 'crop' ? 'cropped' : 'resized';
    const ext = this.selectedFormat === 'jpeg' ? 'jpg' : this.selectedFormat;
    const newFilename = `${nameWithoutExt}-${suffix}.${ext}`;

    // Use cross-browser compatible download helper
    downloadBlob(blob, newFilename);
  }

  destroy(): void {
    this.uploadManager.destroy();
    this.currentImage = null;
    this.currentFile = null;
    this.setInitialized(false);
    console.log('Image Editor Tool destroyed');
  }

  getToolName(): string {
    return this.mode === 'crop' ? 'Image Crop' : 'Image Resize';
  }
}
